import { type KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";
import { useListRef } from "react-window";
import type { AutocompleteRenderInputParams } from "@mui/material";
import { TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import type { MinimalTaskRun } from "src/queries/tasks/tasks.types";
import mergeDeep from "src/utils/object/deepMerge";
import { formatPitForDisplay } from "src/utils/time";
import PitSelectorDefaultRenderOption from "./components/PitSelectorDefaultRenderOption/PitSelectorDefaultRenderOption";
import PitSelectorListbox from "./components/PitSelectorListbox/PitSelectorListbox";
import { filterOptions, SLOT_PROPS } from "./PitSelector.constants";
import type { EnhancedRun, PitSelectorProps } from "./PitSelector.types";
import { enhancePits, getRowHeight, getTotalHeight } from "./PitSelector.utils";

export const PitSelector = ({
  task_id, // if provided, the "value" will be filtered by this "task_id" and not by "pit" prop
  pit,
  pits,
  disabled,
  onChange,
  isLoading,
  open,
  renderOption = PitSelectorDefaultRenderOption,
  placeholder = "Run",
  slots,
  slotProps,
  withReruns = false,
  sx,
  ...props
}: PitSelectorProps) => {
  const enhancedPits = useMemo(
    () => enhancePits(pits, withReruns),
    [pits, withReruns]
  );

  if (typeof pit === "string") {
    pit = { app_pit: pit };
  }

  const [query, setQuery] = useState<string>("");
  const internalListRef = useListRef(null);
  const optionIndexMapRef = useRef<Map<string, number>>(new Map());
  const handleItemsBuilt = useCallback((optionIndexMap: Map<string, number>) => {
    optionIndexMapRef.current = optionIndexMap;
  }, []);

  // Scroll highlighted option into view for keyboard navigation only. MUI also fires
  // onHighlightChange on mouse hover; scrollToRow then jumps (especially on tall rows).
  const handleHighlightChange = (
    _e: React.SyntheticEvent,
    option: EnhancedRun | null,
    reason: string
  ) => {
    if (reason !== "keyboard") return;
    if (option && internalListRef.current) {
      const index = optionIndexMapRef.current.get(String(option.uid));
      if (index !== undefined) {
        internalListRef.current.scrollToRow({ index, align: "auto" });
      }
    }
  };

  const value = useMemo((): EnhancedRun | null => {
    if (!pit) return null;
    const pitValue = typeof pit === "string" ? pit : pit.app_pit;

    return (
      enhancedPits.find((p) => {
        if (task_id) {
          return (p as MinimalTaskRun).task_id === +task_id;
        }
        // Same shortening as route links, so a URL pit matches the API string on the row.
        return (
          !!pitValue &&
          formatPitForDisplay(p.app_pit) === formatPitForDisplay(pitValue)
        );
      }) ?? null
    );
  }, [pit, task_id, enhancedPits]);

  const mergedSlotProps = useMemo(() => {
    return mergeDeep(SLOT_PROPS, slotProps || {}, {
      listbox: {
        RenderOption: renderOption,
        getRowHeight,
        getTotalHeight,
        slotProps: {
          renderOption: slotProps?.renderOption,
          option: slotProps?.option,
        },
        slots,
        ...slotProps?.listbox,
        highlightText: query,
        component: PitSelectorListbox,
        internalListRef,
        onItemsBuilt: handleItemsBuilt,
      },
    });
  }, [slotProps, slots, query, renderOption, handleItemsBuilt, internalListRef]);

  /* Autocomplete<Value, Multiple, DisableClearable, FreeSolo> */
  return (
    <Autocomplete<EnhancedRun, false, true, false>
      loading={isLoading}
      value={value ?? undefined}
      openOnFocus
      disableListWrap
      open={open}
      onInputChange={(_e, value) => setQuery(value)}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          autoFocus={open}
          label={isLoading ? "Fetching pipeline runs…" : placeholder}
          {...slotProps?.renderInput?.TextField}
        />
      )}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
          // Re-dispatch the event at the window level
          window.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
        }
      }}
      onChange={(_e: any, value: any) => {
        onChange?.(value?.app_pit || "");
      }}
      disabled={isLoading || disabled}
      size="small"
      options={enhancedPits || []}
      filterOptions={filterOptions} // determines the filtered options to be rendered on search
      includeInputInList={true}
      isOptionEqualToValue={(option, value) => option.uid === value.uid}
      getOptionKey={(option) => (option as EnhancedRun).uid}
      getOptionLabel={(option) => option.app_pit ?? ""}
      onHighlightChange={handleHighlightChange}
      renderOption={(props, option, state) =>
        [props, option, state.index] as React.ReactNode
      }
      data-test-id="pit-selector"
      {...props}
      slotProps={mergedSlotProps}
      slots={slots}
      disableClearable // disable the "clear" icon which "frees up" the padding-right so the input field has more space for the text
    />
  );
};
